import { Capacitor } from '@capacitor/core';

/**
 * Handle universal links / app links that open the app.
 * Parses the incoming URL and navigates to the correct in-app route.
 * 
 * This supplements the existing Quick Actions handler.
 * Quick Actions handles `exora://` custom scheme shortcuts.
 * This handles `https://exora.club/...` universal links.
 */

let navigateFn: ((path: string) => void) | null = null;

const DEEP_LINK_EVENT = 'exora-deep-link';

/**
 * Called by the router component once mounted to provide navigation capability.
 */
export function setDeepLinkNavigator(fn: (path: string) => void): void {
  navigateFn = fn;
  
  // Flush any pending deep link
  if (pendingDeepLink) {
    const path = pendingDeepLink;
    pendingDeepLink = null;
    console.log(`[DeepLink] flushing pending → ${path}`);
    fn(path);
  }
}

let pendingDeepLink: string | null = null;

/**
 * Extract the path from a universal link URL.
 * e.g. "https://exora.club/hotel/123" → "/hotel/123"
 */
function extractPath(url: string): string | null {
  // Custom scheme: exora://hotel/123
  if (url.startsWith('exora://')) {
    const path = url.replace('exora://', '');
    if (!path || path === 'open') return '/';
    return '/' + path;
  }
  
  // Universal link: https://exora.club/stays/...
  try {
    const parsed = new URL(url);
    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    return null;
  }
}

/**
 * Handle an incoming deep link URL.
 */
export function handleDeepLink(url: string): void {
  const path = extractPath(url);
  if (!path) {
    console.warn(`[DeepLink] could not parse URL: ${url}`);
    return;
  }
  
  console.log(`[DeepLink] resolved path: ${path}`);
  
  if (navigateFn) {
    navigateFn(path);
  } else {
    pendingDeepLink = path;
    console.log(`[DeepLink] router not ready, queued: ${path}`);
    // Also dispatch event for any listeners
    window.dispatchEvent(new CustomEvent(DEEP_LINK_EVENT, { detail: path }));
  }
}

/**
 * Initialize deep link listener for universal links.
 * Custom scheme links are already handled by capacitorQuickActions.
 * This adds handling for https:// universal links.
 */
export function initDeepLinkListener(): void {
  if (!Capacitor.isNativePlatform()) return;
  
  import('@capacitor/app').then(({ App }) => {
    // Listen for universal links (https:// URLs)
    App.addListener('appUrlOpen', ({ url }) => {
      // Skip custom scheme — already handled by Quick Actions
      if (url.startsWith('exora://')) return;
      
      console.log(`[DeepLink] universal link received: ${url}`);
      handleDeepLink(url);
    });
    
    // Check if app was launched via universal link
    App.getLaunchUrl().then((result) => {
      if (!result?.url) return;
      if (result.url.startsWith('exora://')) return; // Handled by Quick Actions
      
      console.log(`[DeepLink] launched via universal link: ${result.url}`);
      handleDeepLink(result.url);
    });
    
    console.log('[DeepLink] Universal link listener registered');
  }).catch((e) => {
    console.warn('[DeepLink] Failed to init', e);
  });
}
